<template>
  <div class="user-edit">
    <h1>{{ user.firstName }} {{ user.middleName }} {{ user.lastName }}</h1>
    <p class="email">
      {{ user.email }}
    </p>
    <div class="actions mb-2">
      <b-button
        v-if="!isOwnProfile"
        variant="primary"
        size="sm"
        class="action-button red mr-3"
        @click="goToMessage(user.id)"
      >
        <b-icon-envelope-fill class="action-icon"></b-icon-envelope-fill>
        send message
      </b-button>
      <b-button
        v-if="canEditNameAndEmail"
        variant="primary"
        size="sm"
        class="action-button red"
        @click="showIdentityValuesEditForm = true"
        :disabled="showIdentityValuesEditForm"
      >
        <b-icon-pencil class="action-icon"></b-icon-pencil>
        edit name and email
      </b-button>
    </div>
    <identity-values-edit-form
      v-show="showIdentityValuesEditForm"
      ref="identityValuesForm"
      :initial-email="this.user.email"
      :initial-first-name="this.user.firstName"
      :initial-middle-name="this.user.middleName"
      :initial-last-name="this.user.lastName"
    ></identity-values-edit-form>
    <template v-if="canEditRoles">
      <p class="role-container">
        <span class="role-name">Mentor:</span>
        <toggle-button
          class="toggle-button"
          :on="user.roles.includes('Mentor')"
          @click="
            user.roles.includes(roleMap.Mentor)
              ? confirmRemove(roleMap.Mentor)
              : confirmAdd(roleMap.Mentor)
          "
        ></toggle-button>
      </p>
      <p class="role-container">
        <span class="role-name">Student:</span>
        <toggle-button
          class="toggle-button"
          :on="user.roles.includes(roleMap.Student)"
          @click="
            user.roles.includes(roleMap.Student)
              ? confirmRemove(roleMap.Student)
              : confirmAdd(roleMap.Student)
          "
        ></toggle-button>
      </p>
    </template>
    <template v-else>
      <p>
        <span
          class="readonly-role-item"
          v-for="role in user.roles"
          :key="role"
          >{{ role }}</span
        >
      </p>
    </template>
    <div class="personal-profile">
      <editable-pronouns
        label="Pronouns"
        name="Pronouns"
        :initial-value="user.pronouns"
        :validator="validators.isNotEmptyString"
        :saver="saveProfileValue"
      ></editable-pronouns>
      <h4>Contact Numbers</h4>
      <ul class="numbers-container">
        <li
          class="number-entry"
          v-for="number in user.phoneNumbers"
          :key="number.id"
        >
          <editable-phone-number
            :phone-number-types="phoneNumberTypes"
            :validator="validators.isValidPhoneNumber"
            :initial-value="number"
            :name="number.id"
            :saver="savePhoneNumberValue"
            @delete="handleDeletePhoneNumber"
          ></editable-phone-number>
        </li>
      </ul>
      <editable-phone-number
        v-if="phoneFormState !== phoneFormStates.hidden"
        ref="newPhoneField"
        :phone-number-types="phoneNumberTypes"
        :validator="validators.alwaysValid"
        :initial-value="{}"
        :initial-mode="editableFieldModes.edit"
        :name="0"
        :is-deletable="false"
        :readonly="phoneFormState === phoneFormStates.processing"
        :show-buttons="phoneFormState === phoneFormStates.editing"
        :saver="createNewPhoneNumberSaver"
        :is-new-phone-form="true"
        @cancel-new-phone-number-form="cancelPhoneNumberForm"
      ></editable-phone-number>
      <b-button
        class="phone-number-add-button"
        :disabled="phoneFormState !== phoneFormStates.hidden"
        @click="phoneFormState = phoneFormStates.editing"
        ><b-icon-plus-circle-fill></b-icon-plus-circle-fill> add phone
        number</b-button
      >
      <editable-text-area
        class="bio-field"
        label="Bio"
        name="Bio"
        :initial-value="user.bio"
        :validator="validators.isNotEmptyString"
        :saver="saveProfileValue"
      ></editable-text-area>
    </div>

    <profile-section
      ref="mentorProfile"
      v-if="user.roles.includes(roleMap.Mentor)"
      class="mentor-profile"
      :field-metadata="mentorMetadata"
    ></profile-section>
    <profile-section
      ref="viewProfile"
      v-if="user.roles.includes(roleMap.Student)"
      class="student-profile"
      :field-metadata="studentMetadata"
    ></profile-section>
    <confirmation-modal
      :title="modalData.title"
      :question="modalData.question"
      :yesActionText="modalData.yesActionText"
      :noActionText="modalData.noActionText"
      :yesCallback="modalData.yesCallback"
      :noCallback="modalData.noCallback"
      ref="confirmation-modal"
    ></confirmation-modal>
  </div>
</template>

<script>
import ToggleButton from "../forms/ToggleButton";
import MentorApplicationApi from "../lib/MentorApplicationApi.js";
import ProfileSection from "./ProfileSection";
import ConfirmationModal from "../forms/ConfirmationModal.vue";
import {
  BButton,
  BIconEnvelopeFill,
  BIconPencil,
  BIconPlusCircleFill
} from "bootstrap-vue";
import EditablePronouns from "../forms/EditablePronouns.vue";
import EditablePhoneNumber from "../forms/EditablePhoneNumber";
import { Modes } from "../forms/EditableBase";
import IdentityValuesEditForm, {
  Events as IVFEvents
} from "@/components/user-edit/IdentityValuesEditForm";
import {
  IsValidEmail,
  AlwaysValidValidator,
  IsNotEmptyString,
  IsValidPhoneNumber
} from "../lib/Validation";
import { Types } from "./ProfileItem";
import { RightTrim } from "../lib/Sanitation";
import { RoleModalMessages } from "../lib/MessageBuilder";
import {
  RoleMap,
  MonthsSelectOptions,
  SemesterSelectOptions
} from "../lib/Constants";
import {
  ViewProfileEvent,
  MessageUserEvent,
  RemoveUserEvent
} from "./LinkedUser";
import { AddUserLinkEvent } from "./UserLinker";
import EditableTextArea from "@/components/forms/EditableTextArea";

const AssignedStudentsLabel = "Assigned Students";
const AssignedMentorLabel = "Assigned Mentor";

export default {
  name: "UserProfileEditView",
  components: {
    EditableTextArea,
    EditablePronouns,
    EditablePhoneNumber,
    ToggleButton,
    ProfileSection,
    ConfirmationModal,
    BIconEnvelopeFill,
    BIconPlusCircleFill,
    BButton,
    BIconPencil,
    IdentityValuesEditForm
  },
  props: {
    user: {
      type: Object,
      default: () => {}
    },
    apiRootUrl: { type: String, required: true },
    userAdminUrl: { type: String, required: true },
    messageRootUrl: { type: String, required: true },
    changeMajorFormUrl: { type: String, required: true },
    canEditMentor: { type: Boolean, default: true },
    canEditStudents: { type: Boolean, default: true },
    canEditRoles: { type: Boolean, default: true },
    // TODO deal with first, middle, last names and email all separately, via the "normal" readonly fields list
    canEditNameAndEmail: { type: Boolean, default: true },
    isOwnProfile: { type: Boolean, default: false },
    userProfileUrl: { type: String, required: false },
    phoneNumberTypes: { type: Array, required: true },
    readOnlyProperties: {
      type: Object,
      default: () => {
        return {
          user: [],
          student: [],
          mentor: []
        };
      }
    }
  },
  data() {
    return {
      showIdentityValuesEditForm: false,
      roleMap: RoleMap,
      activeProfile: "",
      mentorApplicationApi: new MentorApplicationApi(this.apiRootUrl),
      modalData: {
        title: "",
        question: "",
        yesActionText: "",
        noActionText: "",
        yesCallback: function() {},
        noCallback: function() {}
      },
      mentorMetadata: [],
      studentMetadata: [],
      validators: {
        isNotEmptyString: IsNotEmptyString,
        alwaysValid: AlwaysValidValidator,
        isValidPhoneNumber: IsValidPhoneNumber
      },
      phoneFormStates: {
        hidden: "hidden",
        editing: "editing",
        processing: "processing"
      },
      phoneFormState: "hidden",
      phoneNumberTypeOptions: this.phoneNumberTypes.map(x => {
        return { value: x, text: x };
      }),
      editableFieldModes: Modes
    };
  },
  mounted() {
    // set event listeners on self -- with EmitUp, they will be called from self
    this.$on(ViewProfileEvent, this.handleViewProfile);
    let that = this;
    this.$on(MessageUserEvent, e => that.goToMessage(e.user.id));
    this.$on(RemoveUserEvent, this.handleRemoveUser);
    this.$on(AddUserLinkEvent, this.handleAddUserLink);
    this.$refs.identityValuesForm.$on(
      IVFEvents.save,
      this.handleSaveIdentityValues
    );
    this.$refs.identityValuesForm.$on(IVFEvents.cancel, () => {
      that.showIdentityValuesEditForm = false;
      that.$refs.identityValuesForm.reset();
    });
    let mentorSectionFields = [
      {
        type: Types.user,
        initialValue: this.user.mentor,
        relationship: RoleMap.Mentor,
        readonly: !this.canEditMentor // invert the canEdit value to get the readonly value
      }
    ];
    if (this.canEditMentor) {
      mentorSectionFields.push({
        type: Types.userLinker,
        relationship: RoleMap.Mentor,
        fieldProps: {
          apiRootUrl: this.apiRootUrl,
          isVisible:
            "object" !== typeof this.user.mentor || null === this.user.mentor
        }
      });
    }
    this.studentMetadata = [
      {
        type: Types.section,
        label: AssignedMentorLabel,
        fields: mentorSectionFields
      },
      {
        type: Types.section,
        label: "Student Data",
        fields: []
      },
      {
        name: "studentStartYear",
        label: "Mentor Program Start Year",
        initialValue: this.user.studentProfile?.studentStartYear + "",
        type: Types.textField,
        // TODO add validator!
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("studentStartYear"),
        fieldProps: {}
      },
      {
        name: "studentStartMonth",
        label: "Mentor Program Start Month",
        initialValue: this.user.studentProfile?.studentStartMonth + "",
        type: Types.select,
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("studentStartMonth"),
        fieldProps: {
          options: MonthsSelectOptions
        }
      },
      {
        name: "major",
        label: "Major",
        validator: IsNotEmptyString,
        saver: this.saveStudentValue,
        initialValue: this.user.studentProfile?.major,
        readonly: this.readOnlyProperties.student.includes("major"),
        type: Types.textField,
        fieldProps: {},
        afterHtml:
          this.isOwnProfile && this.readOnlyProperties.student.includes("major")
            ? `<p class="text-muted small ml-3" style="margin-top: -1rem;">&bull; <a href="${this.changeMajorFormUrl}">Please use this form to let us know if you are changing your major</a>.</p>`
            : null
      },
      {
        name: "isPartee",
        label: "Is a PartEE Student?",
        initialValue: this.user.studentProfile?.isPartee,
        type: Types.boolField,
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("isPartee"),
        fieldProps: {}
      },
      {
        name: "transferSchool",
        label: "Transfer School",
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        initialValue: this.user.studentProfile?.transferSchool, /////// was set to a default....
        readonly: this.readOnlyProperties.student.includes("transferSchool"),
        type: Types.textField,
        fieldProps: {}
      },
      {
        name: "fsAdmitYear",
        label: "Fresno State Admission Year",
        initialValue: this.user.studentProfile?.fsAdmitYear + "",
        type: Types.textField,
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("fsAdmitYear"),
        fieldProps: {}
      },
      {
        name: "fsAdmitSemester",
        label: "Fresno State Admission Semester",
        initialValue: this.user.studentProfile?.fsAdmitSemester + "",
        type: Types.select,
        validator: AlwaysValidValidator,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("fsAdmitSemester"),
        fieldProps: {
          options: SemesterSelectOptions
        }
      },
      {
        name: "officialSchoolEmail",
        label: "Official School Email",
        initialValue: this.user.studentProfile?.officialSchoolEmail,
        type: Types.textField,
        validator: IsValidEmail,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes(
          "officialSchoolEmail"
        ),
        fieldProps: {}
      },
      {
        name: "studentIdNumber",
        label: "Student ID Number",
        initialValue: this.user.studentProfile?.studentIdNumber + "", // coerce number to string
        type: Types.textField,
        validator: IsNotEmptyString, // TODO more appropriate validator
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("studentIdNumber"),
        fieldProps: {}
      },
      {
        name: "homeAddress",
        label: "Home Address",
        initialValue: this.user.studentProfile?.homeAddress,
        type: Types.textArea,
        validator: IsNotEmptyString,
        saver: this.saveStudentValue,
        readonly: this.readOnlyProperties.student.includes("homeAddress"),
        fieldProps: {}
      }
    ];

    let assignedStudentsSectionFields = [
      {
        type: Types.userList,
        initialValue: this.user.students,
        relationship: RoleMap.Student,
        readonly: !this.canEditStudents // invert the canEdit value to get the readonly value
      }
    ];
    if (this.canEditStudents) {
      assignedStudentsSectionFields.push({
        type: Types.userLinker,
        relationship: RoleMap.Student,
        fieldProps: {
          apiRootUrl: this.apiRootUrl
        }
      });
    }
    this.mentorMetadata = [
      {
        type: Types.section,
        label: AssignedStudentsLabel,
        fields: assignedStudentsSectionFields
      },
      {
        type: Types.section,
        label: "Mentor Data",
        fields: []
      },
      {
        name: "mentorStartYear",
        label: "Mentor Program Start Year",
        initialValue: this.user.mentorProfile?.mentorStartYear + "",
        type: Types.textField,
        // TODO add validator!
        validator: AlwaysValidValidator,
        saver: this.saveMentorValue,
        readonly: this.readOnlyProperties.mentor.includes("mentorStartYear"),
        fieldProps: {}
      },
      {
        name: "mentorStartMonth",
        label: "Mentor Program Start Month",
        initialValue: this.user.mentorProfile?.mentorStartMonth + "",
        type: Types.select,
        validator: AlwaysValidValidator,
        saver: this.saveMentorValue,
        readonly: this.readOnlyProperties.mentor.includes("mentorStartMonth"),
        fieldProps: {
          options: MonthsSelectOptions
        }
      },
      {
        name: "specialty",
        label: "Major / Specialty",
        initialValue: this.user.mentorProfile?.specialty,
        type: Types.textField,
        validator: IsNotEmptyString,
        saver: this.saveMentorValue,
        readonly: this.readOnlyProperties.mentor.includes("specialty"),
        fieldProps: {}
      },
      {
        name: "company",
        label: "Company",
        initialValue: this.user.mentorProfile?.company,
        type: Types.textField,
        validator: IsNotEmptyString,
        saver: this.saveMentorValue,
        readonly: this.readOnlyProperties.mentor.includes("company"),
        fieldProps: {}
      },
      {
        name: "professionalAddress",
        label: "Business Name and Address",
        initialValue: this.user.mentorProfile?.professionalAddress,
        type: Types.textArea,
        validator: IsNotEmptyString,
        saver: this.saveMentorValue,
        readonly: this.readOnlyProperties.mentor.includes(
          "professionalAddress"
        ),
        fieldProps: {}
      }
    ];
  },
  methods: {
    cancelPhoneNumberForm() {
      this.phoneFormState = this.phoneFormStates.hidden;
    },
    handleDeletePhoneNumber(data) {
      let that = this;
      this.mentorApplicationApi
        .deletePhoneNumber(this.user.id, data.id)
        .then(
          () => {
            that.user.phoneNumbers = that.user.phoneNumbers.filter(
              x => x.id !== data.id
            );
          },
          error => {
            alert(
              `Error removing phone number: ${
                error.response.data.error
                  ? error.response.data.error
                  : "unknown error"
              }`
            );
            data.failureCallback();
          }
        )
        .catch(() => {
          alert("Unable to remove phone number for unknown reasons");
          data.failureCallback();
        });
    },
    handleSaveIdentityValues(payload) {
      let that = this;
      if (0 === Object.keys(payload).length) {
        // leave the edit screen as though saved.
        that.$refs.identityValuesForm.reset();
        that.showIdentityValuesEditForm = false;
        return;
      }
      this.mentorApplicationApi
        .bulkUpdateUserProfileProps(this.user.id, payload)
        .then(
          result => {
            that.user.email = result.data.user.email;
            that.$refs.identityValuesForm.initialEmail = result.data.user.email;
            that.user.firstName = result.data.user.firstName;
            that.$refs.identityValuesForm.initialFirstName =
              result.data.user.firstName;
            that.user.middleName = result.data.user.middleName;
            that.$refs.identityValuesForm.initialMiddleName =
              result.data.user.middleName;
            that.user.lastName = result.data.user.lastName;
            that.$refs.identityValuesForm.initialLastName =
              result.data.user.lastName;
            that.showIdentityValuesEditForm = false;
            that.$refs.identityValuesForm.reset();
          },
          error => {
            alert(
              `Error saving changes: ${
                error.response.data.error
                  ? error.response.data.error
                  : "unknown error"
              }`
            );
          }
        )
        .catch(() => {
          alert("Unable to save changes for unknown reasons");
        });
    },
    alert: msg => alert(msg),
    goToMessage(id) {
      window.location = `${RightTrim(
        "/",
        this.messageRootUrl
      )}/${encodeURIComponent(id)}`;
    },
    extractRelationshipData(addRemoveUserLinkPayload) {
      return {
        mentorId:
          addRemoveUserLinkPayload.relationship === RoleMap.Student
            ? this.user.id
            : addRemoveUserLinkPayload.user.id,
        studentId:
          addRemoveUserLinkPayload.relationship === RoleMap.Student
            ? addRemoveUserLinkPayload.user.id
            : this.user.id,
        studentEmail:
          addRemoveUserLinkPayload.relationship === RoleMap.Student
            ? addRemoveUserLinkPayload.user.email
            : this.user.email
      };
    },
    handleAddUserLink(payload) {
      let that = this,
        data = this.extractRelationshipData(payload);
      // check to make sure the users aren't already linked
      if (
        (RoleMap.Student === payload.relationship &&
          that.user.students.some(x => x.id === payload.user.id)) ||
        (RoleMap.Mentor === payload.relationship &&
          that.user.mentor?.id === payload.user.id)
      ) {
        alert("That student/mentor association already exists");
        payload.handledCallback();
        return;
      }
      this.mentorApplicationApi
        .addStudentMentorLink(data.mentorId, data.studentEmail, data.studentId)
        .then(
          result => {
            if (RoleMap.Student === payload.relationship) {
              that.user.students.push(result.data.student);
            } else {
              that.user.mentor = result.data.mentor;
              // unfortunately, the above won't mutate the profile field -- so let's do it manually
              // TODO find a way to do all of this bookkeeping in a more natural, reactive way
              let fields = that.studentMetadata.find(
                x => AssignedMentorLabel === x.label
              )?.fields;
              // insert the new mentor
              fields.push({
                type: Types.user,
                initialValue: that.user.mentor,
                relationship: RoleMap.Mentor
              });
              // hide the user linker
              fields.find(
                x => Types.userLinker === x.type
              ).fieldProps.isVisible = false;
            }
            payload.handledCallback(true);
          },
          error => {
            alert(`Error removing linked user: ${error.response.data.error}`);
            payload.handledCallback();
          }
        )
        .catch(e => {
          console.log(e);
          debugger;
          alert("Unable to add user for unknown reasons");
          payload.handledCallback();
        });
    },
    handleRemoveUser(payload) {
      let that = this,
        data = this.extractRelationshipData(payload);
      this.mentorApplicationApi
        .removeStudentMentorLink(
          data.mentorId,
          data.studentEmail,
          data.studentId
        )
        .then(
          // success -- no need to call handledCallback, as the component will be removed
          () => {
            if (payload.relationship === RoleMap.Student) {
              // mutate students array in place; the change will be picked up by the profile automatically,
              // as it references the array
              that.user.students.splice(
                that.user.students.findIndex(x => x.id === data.studentId),
                1
              );
            } else {
              // unfortunately, removing the value from the user object doesn't mutate the object in the profile
              that.user.mentor = null;
              // so, remove it manually using splice to mutate the array in place
              let fields = that.studentMetadata.find(
                x => AssignedMentorLabel === x.label
              ).fields;
              fields.splice(
                fields.findIndex(x => "User" === x.type),
                1
              );
              fields.find(
                x => "UserLinker" === x.type
              ).fieldProps.isVisible = true;
            }
          },
          error => {
            alert(`Error removing linked user: ${error.response.data.error}`);
            payload.handledCallback();
          }
        )
        .catch(() => {
          alert("Unable to remove user for unknown reasons");
          payload.handledCallback();
        });
    },
    handleViewProfile: function(e) {
      // no need to call handledCallback, as we are moving to a new page completely
      // below uses the canEdit flags to determine which kind of profile to go to -- the edit or view profile
      let url =
        this.canEditStudents && this.canEditMentor
          ? this.userAdminUrl
          : this.userProfileUrl;
      window.location.href = `${RightTrim("/", url)}/${e.user.id}`;
    },
    /**
     * Hijack the saver API used by the EditableX fields to create a new item
     * @param resolve
     * @param reject
     * @param uselessNameIgnoreIt
     * @param phoneNumberData
     */
    createNewPhoneNumberSaver(
      resolve,
      reject,
      uselessNameIgnoreIt,
      phoneNumberData
    ) {
      let that = this;
      this.mentorApplicationApi
        .addPhoneNumber(this.user.id, phoneNumberData)
        .then(
          result => {
            // remove preferred from all other numbers if the new number is preferred
            if (result.data.newPhoneNumber.isPreferredContactNumber) {
              that.user.phoneNumbers.forEach(
                p => (p.isPreferredContactNumber = false)
              );
            }
            that.user.phoneNumbers.push(result.data.newPhoneNumber);
            resolve(result);
            that.phoneFormState = that.phoneFormStates.hidden;
          },
          error => {
            alert(
              `Unable to add the phone number: ${error.response.data.error}`
            );
            reject();
          }
        )
        .catch(() => {
          reject();
          that.phoneFormState = that.phoneFormStates.editing;
        });
    },
    savePhoneNumberValue(resolve, reject, phoneNumberId, changedProperties) {
      let that = this;
      this.mentorApplicationApi
        .updatePhoneNumber(this.user.id, phoneNumberId, changedProperties)
        .then(result => {
          resolve(result);
          if (
            "undefined" !== typeof changedProperties.isPreferredContactNumber
          ) {
            if (result.data.phoneNumber.isPreferredContactNumber) {
              // set preferred contact number flag to false for all phoneNumbers
              that.user.phoneNumbers.forEach(
                x => (x.isPreferredContactNumber = false)
              );
            }
            // update all of the values for this phone number to match what was returned from
            // the API call
            let previousPhoneNumber = that.user.phoneNumbers.find(
              x => x.id === phoneNumberId
            );
            Object.keys(changedProperties).forEach(key => {
              previousPhoneNumber[key] = result.data.phoneNumber[key];
            });
          }
        })
        .catch(() => reject("server error - unable to update"));
    },
    saveProfileValue(resolve, reject, name, value) {
      this.mentorApplicationApi.propertySaver(
        resolve,
        reject,
        this.user.id,
        this.mentorApplicationApi.profiles.main,
        name,
        value,
        this.user
      );
    },
    saveMentorValue(resolve, reject, name, value) {
      this.mentorApplicationApi.propertySaver(
        resolve,
        reject,
        this.user.id,
        this.mentorApplicationApi.profiles.mentor,
        name,
        value,
        this.user.mentorProfile
      );
    },
    saveStudentValue(resolve, reject, name, value) {
      this.mentorApplicationApi.propertySaver(
        resolve,
        reject,
        this.user.id,
        this.mentorApplicationApi.profiles.student,
        name,
        value,
        this.user.studentProfile
      );
    },
    confirmRemove: function(roleName) {
      const that = this;
      this.modalData = {
        title: RoleModalMessages.RemoveTitle(),
        question: RoleModalMessages.RemoveRole(this.user, roleName),
        yesActionText: RoleModalMessages.RemoveRoleYesAction(roleName),
        noActionText: RoleModalMessages.CommonCancelAction(),
        yesCallback: () => that.removeRole(roleName),
        noCallback: () => that.$refs["confirmation-modal"].resetProperties()
      };
      this.$refs["confirmation-modal"].showModal();
    },
    confirmAdd: function(roleName) {
      const that = this;
      this.modalData = {
        title: RoleModalMessages.AddTitle(),
        question: RoleModalMessages.AddRole(this.user, roleName),
        yesActionText: RoleModalMessages.AddRoleYesAction(roleName),
        noActionText: RoleModalMessages.CommonCancelAction(),
        yesCallback: () => that.addRole(roleName),
        noCallback: () => that.$refs["confirmation-modal"].resetProperties()
      };
      this.$refs["confirmation-modal"].showModal();
    },
    maybeCleanUserLinks() {
      if (!this.user.roles.includes(RoleMap.Mentor)) {
        this.user.students = [];
      }
      if (!this.user.roles.includes(RoleMap.Student)) {
        this.user.mentor = null;
      }
    },
    addRole: function(role) {
      let that = this;
      this.mentorApplicationApi
        .addRole(this.user.id, this.user.email, role)
        .then(
          response => {
            that.user.roles = response.data.roles;
            that.maybeCleanUserLinks();
            location.reload();
          },
          error => {
            alert(
              `Unable to add the role ${role}: ${error.response.data.error}`
            );
          }
        )
        .catch(() =>
          alert(`Unable to add the role ${role} due to unknown reasons.`)
        );
    },
    removeRole: function(role) {
      let that = this;
      this.mentorApplicationApi
        .removeRole(this.user.id, this.user.email, role)
        .then(
          response => {
            that.user.roles = response.data.roles;
            that.maybeCleanUserLinks();
            location.reload();
          },
          error => {
            alert(
              `Unable to remove the role ${role}: ${error.response.data.error}`
            );
          }
        )
        .catch(() =>
          alert(`Unable to remove the role ${role} due to unknown reasons`)
        );
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.readonly-role-item {
  margin-right: 1rem;
}
.user-edit {
  text-align: left;
}
.role-container {
  vertical-align: middle;
}
.role-name {
  vertical-align: middle;
  display: inline-block;
  font-weight: bold;
}
.email {
  font-style: italic;
}
.toggle-button {
  scale: 80%;
  vertical-align: middle;
  display: inline-block;
  margin: 0;
}
.bio-field {
  margin-top: 2rem;
}
</style>
